class SwiperCarousel extends HTMLElement {
  constructor() {
    super();

    this.id = this.dataset.id;
    this.swiperEl = this.querySelector(`.swiper-carousel`);
    this._initSwiper = this._initSwiper.bind(this);
    this._onResize = this._onResize.bind(this);
    this._scheduleReinit = window.createSwiperReinitScheduler(
      this._initSwiper,
      100,
    );
    this._setupSwiperLoadingLogic();
    this.fullWidth = this.dataset.fullWidth === "true";
    this._onBreakpoint = null;
    window.addEventListener("resize", this._onResize);
  }

  _setupSwiperLoadingLogic() {
    if (!this.swiperEl) return;
    this._swiperLoaderReadyHandler = window.initSwiperWhenReadyAndVisible({
      element: this,
      init: this._initSwiper,
      threshold: 0.1,
      hiddenThreshold: 0,
    });
  }

  _onResize() {
    if (!this.swiper || !window.Swiper) return;
    this._scheduleReinit(window.Swiper);
  }

  _initSwiper(Swiper) {
    if (!Swiper) return;

    if (this.swiper) {
      if (this._onBreakpoint) {
        this.swiper.off("breakpoint", this._onBreakpoint);
      }
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    const swiperId = "swiper-carousel-" + this.id;
    const arrowLeft = this.querySelector(".swiper-button-prev");
    const arrowRight = this.querySelector(".swiper-button-next");
    if (arrowLeft && arrowRight) {
      arrowLeft.classList.add(`${swiperId}-arrow-left`);
      arrowRight.classList.add(`${swiperId}-arrow-right`);
    }
    let carouselLeftOffset = 0;
    let carouselRightOffset = 0;
    this.swiperEl.classList.add(swiperId);
    const containerEl = this.swiperEl.querySelector(".container");
    if (containerEl && !this.fullWidth) {
      const containerRect = containerEl.getBoundingClientRect();
      const containerStyles = window.getComputedStyle(containerEl);
      const paddingLeft = Number.parseFloat(containerStyles.paddingLeft) || 0;
      const paddingRight = Number.parseFloat(containerStyles.paddingRight) || 0;
      carouselLeftOffset = Math.max(0, containerRect.left) + paddingLeft;
      carouselRightOffset = Math.max(0, window.innerWidth - containerRect.right) + paddingRight;
    }
    carouselRightOffset += parseInt(this.dataset.rightOffset) || 0;
    const spaceBetween = parseInt(this.dataset.spaceBetween) || 0;
    const config = {
      slidesPerView: 1.5,
      centeredSlides: false,
      loop: false,
      infinite: false,
      spaceBetween: spaceBetween,
      observer: true,
      observeParents: true,
      setWrapperSize: true,
      slidesOffsetAfter: carouselRightOffset,
      slidesOffsetBefore: carouselLeftOffset,
      breakpoints: {
        320: {
          slidesPerView: 1.5,
          slidesPerGroup: 1,
        },
        768: {
          slidesPerView: 2.5,
          slidesPerGroup: 2,
        },
        940: {
          slidesPerView: 3.5,
          slidesPerGroup: 3,
        },
        1200: {
          slidesPerView: 4.5,
          slidesPerGroup: 4,
        },
        1600: {
          slidesPerView: 5.5,
          slidesPerGroup: 6,
        },
      },
      navigation: {
        nextEl: `.${swiperId}-arrow-right`,
        prevEl: `.${swiperId}-arrow-left`,
      }
    };
    this.swiper = new Swiper(`.${swiperId}`, config);
    this._onBreakpoint = () => this._scheduleReinit(Swiper);
    this.swiper.on("breakpoint", this._onBreakpoint);

    window.requestAnimationFrame(() => {
      const slides = this.swiper.slides;
      if (!slides || slides.length === 0) return;
      const biggestHeight = Math.max(
        ...slides.map((s) => s.offsetHeight)
      );
      //create a css variable for the height
      this.style.setProperty("--slides-height", `${biggestHeight}px`);
    });
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this._onResize);
    if (this._swiperLoaderReadyHandler) {
      window.removeEventListener(
        "swiperloader:ready",
        this._swiperLoaderReadyHandler,
      );
    }
    this._scheduleReinit.cancel?.();
  }

  // Public method to manually trigger initialization
  init() {
    window.initSwiperWhenReadyAndVisible({
      element: this,
      init: this._initSwiper,
      threshold: 0.1,
      hiddenThreshold: 0,
    });
  }

  // Force initialization immediately, bypassing visibility checks
  initImmediately() {
    if (window.swiperLoader) {
      window.swiperLoader.initImmediately(this._initSwiper);
    }
  }
}

customElements.define("swiper-carousel", SwiperCarousel);
